import React from 'react';
import { useI18n } from '../../i18n/I18nContext';
import './Pagination.css';

/**
 * Stateless pagination control. Pair with the `usePagination` hook:
 *
 *   const pagination = usePagination();
 *   <Pagination state={pagination} />
 *
 * Or pass primitives if state lives elsewhere.
 */
function Pagination({ state, page, totalPages, onPrev, onNext, total, limit, hideOnSinglePage = false }) {
  const { t } = useI18n();
  const currentPage = state?.page ?? page ?? 1;
  const lastPage = state?.totalPages ?? totalPages ?? 1;
  const handlePrev = state?.prev ?? onPrev;
  const handleNext = state?.next ?? onNext;
  const totalCount = state?.total ?? total;
  const pageSize = state?.limit ?? limit;

  if (hideOnSinglePage && lastPage <= 1) return null;

  // Compact "x–y of z" range when a page size is known; falls back to a
  // plain "Page x of y" (+ optional total) otherwise.
  let info;
  if (pageSize && totalCount) {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalCount);
    info = t('ui.paginationRange', { start, end, total: totalCount });
  } else {
    info = t('ui.pageOfTotal', { page: currentPage, total: Math.max(1, lastPage) })
      + (totalCount ? ` · ${t('ui.totalCount', { count: totalCount })}` : '');
  }

  return (
    <div className="ds-pagination">
      <button
        type="button"
        onClick={handlePrev}
        disabled={currentPage <= 1}
        className="ds-pagination-btn"
      >
        {t('ui.previous')}
      </button>
      <span className="ds-pagination-info">{info}</span>
      <button
        type="button"
        onClick={handleNext}
        disabled={currentPage >= lastPage}
        className="ds-pagination-btn"
      >
        {t('ui.next')}
      </button>
    </div>
  );
}

export default Pagination;
